import React, { useEffect, useMemo, useState } from "react";
import { Modal, Form, Input, Button, Upload, Row, Col, Select, Checkbox, Divider, Tabs, ColorPicker, Switch, message } from "antd";
import { UploadOutlined, MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import { EstablishmentLocation } from "../../types";
import { ActivationStatus } from "../../types";
import { useAppDispatch, useAppSelector } from "../../utils/hooks/redux";
import { selectEstablishmentType } from "../../redux/features/establishmentType/establishmentTypeSlice";
import { selectPricingPlan } from "../../redux/features/pricingPlan/pricingPlanTypeSlice";
import { fetchPricingPlan } from "../../redux/features/pricingPlan/pricingPlanAsyncThunk";
import { UploadFile } from "antd/es/upload/interface";

interface EstablishmentLocationModalProps {
    visible: boolean;
    onCancel: () => void;
    onSave: (values: any) => void;
    editingRecord: EstablishmentLocation | null;
    establishmentTypeId: string;
    loading: boolean;
}

const imageBeforeUpload = (file: File) => {
    const isAllowedType = ["image/png", "image/jpg", "image/jpeg", "image/webp"].includes(file.type);
    if (!isAllowedType) return Upload.LIST_IGNORE;
    const isLt5MB = file.size / 1024 / 1024 < 5;
    if (!isLt5MB) return Upload.LIST_IGNORE;
    return false;
};

const EstablishmentLocationModal: React.FC<EstablishmentLocationModalProps> = ({
    visible,
    onCancel,
    onSave,
    editingRecord,
    establishmentTypeId,
    loading,
}) => {
    const [form] = Form.useForm();
    const dispatch = useAppDispatch();
    const { data: establishmentTypes } = useAppSelector(selectEstablishmentType);
    const { data: allPricingPlans } = useAppSelector(selectPricingPlan);
    const [specificTypeOptions, setSpecificTypeOptions] = React.useState<string[]>([]);
    const [heroImageFile, setHeroImageFile] = React.useState<any>(null);
    // Per-location "Time is not on your side…" CMS override. Mirrors heroImageFile —
    // tracks the antd Upload fileList for the optional image, plus the form-level
    // timeNotContent.* fields drive the text inputs.
    const [timeNotImageFile, setTimeNotImageFile] = React.useState<any>(null);
    const [activeTab, setActiveTab] = useState("basic");
    const [schemaDataText, setSchemaDataText] = useState<string>("");
    const [schemaDataError, setSchemaDataError] = useState<string>("");
    const enableLandingPage = Form.useWatch("enableLandingPage", form);
    const selectedLanguages: string[] = Form.useWatch("availableLanguages", form) || ["en"];
    const languageOptions = useMemo(
        () => [
            { code: "en", label: "English" },
            { code: "es", label: "Spanish" },
            { code: "fr", label: "French" },
            { code: "de", label: "German" },
            { code: "pt", label: "Portuguese" },
            { code: "ar", label: "Arabic" },
        ],
        []
    );

    // Watch for changes in establishment type selection
    const selectedTypeIds = Form.useWatch('establishmentTypeIds', form);

    useEffect(() => {
        if (selectedTypeIds && selectedTypeIds.length > 0) {
            const selectedTypes = establishmentTypes
                .filter(type => selectedTypeIds.includes(type.id))
                .flatMap(type => {
                    if (!type.specificTypes) return [];
                    return type.specificTypes.map(st => typeof st === 'string' ? st : st.name);
                });
            const uniqueTypes = Array.from(new Set(selectedTypes)).filter(t => t && t.trim());
            setSpecificTypeOptions(uniqueTypes);
            const currentSpecificType = form.getFieldValue('specificType');
            if (currentSpecificType && !uniqueTypes.includes(currentSpecificType)) {
                form.setFieldsValue({ specificType: undefined });
            }
        } else {
            setSpecificTypeOptions([]);
            form.setFieldsValue({ specificType: undefined });
        }
    }, [selectedTypeIds, establishmentTypes, form]);

    useEffect(() => {
        if (visible && (!allPricingPlans || allPricingPlans.length === 0)) {
            dispatch(fetchPricingPlan());
        }
    }, [visible, dispatch, allPricingPlans]);

    useEffect(() => {
        if (visible) {
            setActiveTab("basic");
            const selectedType = establishmentTypes.find(t => t.id === establishmentTypeId);
            const defaultLanguages = Array.from(new Set([...(selectedType as any)?.availableLanguages || ["en"], "en"]));
            const buildHeroFile = (url?: string): UploadFile | null => {
                if (!url) return null;
                const name = url.split('/').pop() || 'Hero Image';
                return { uid: `${editingRecord?.id || 'new'}-hero`, name, status: 'done', url };
            };
            const buildTimeNotFile = (url?: string): UploadFile | null => {
                if (!url) return null;
                const name = url.split('/').pop() || 'TimeNot Image';
                return { uid: `${editingRecord?.id || 'new'}-timenot`, name, status: 'done', url };
            };

            if (editingRecord) {
                const typeIds = editingRecord.establishmentTypes?.map(t => t.id) || [];
                const heroContentForForm = { ...((editingRecord as any)?.heroContent || {}) };

                // Convert column icon URLs to Upload fileList format
                if (heroContentForForm?.searchDetails?.columns) {
                    heroContentForForm.searchDetails = {
                        ...heroContentForForm.searchDetails,
                        columns: heroContentForForm.searchDetails.columns.map((col: any, idx: number) => ({
                            ...col,
                            icon: col.icon && typeof col.icon === 'string'
                                ? [{ uid: `col-icon-${idx}`, name: col.icon.split('/').pop() || 'icon', status: 'done' as const, url: col.icon }]
                                : col.icon || [],
                        })),
                    };
                }

                form.setFieldsValue({
                    ...editingRecord,
                    establishmentTypeIds: typeIds.length > 0 ? typeIds : [editingRecord.establishmentTypeId],
                    icon: editingRecord.fullUrl ? [{ url: editingRecord.fullUrl, name: editingRecord.icon }] : [],
                    enableLandingPage: !!(editingRecord as any)?.heroContent,
                    availableLanguages: Array.from(new Set([...(editingRecord as any)?.availableLanguages || defaultLanguages, "en"])),
                    // Normalize to an array; Checkbox.Group doesn't bind to null and the field
                    // would otherwise never register in form state, so validateFields() omits
                    // it and the update sends no pricingPlanIds at all.
                    pricingPlanIds: Array.isArray((editingRecord as any)?.pricingPlanIds)
                        ? (editingRecord as any).pricingPlanIds
                        : [],
                    heroContent: heroContentForForm,
                    timeNotContent: (editingRecord as any)?.timeNotContent || {},
                    timeNotContentTranslations: (editingRecord as any)?.timeNotContentTranslations || {},
                });
                const heroContent = (editingRecord as any)?.heroContent;
                if (heroContent?.heroImageUrl) {
                    setHeroImageFile(buildHeroFile(heroContent.heroImageUrl));
                } else {
                    setHeroImageFile(null);
                }
                const timeNotContent = (editingRecord as any)?.timeNotContent;
                if (timeNotContent?.imageUrl) {
                    setTimeNotImageFile(buildTimeNotFile(timeNotContent.imageUrl));
                } else {
                    setTimeNotImageFile(null);
                }
                const existingSchema = heroContent?.seo?.schemaData;
                setSchemaDataText(existingSchema ? JSON.stringify(existingSchema, null, 2) : "");
                setSchemaDataError("");
            } else {
                form.resetFields();
                form.setFieldsValue({
                    establishmentTypeIds: [establishmentTypeId],
                    enableLandingPage: false,
                    showInFooter: false,
                    availableLanguages: defaultLanguages,
                    pricingPlanIds: [],
                    heroContent: {},
                    timeNotContent: {},
                    timeNotContentTranslations: {},
                });
                setHeroImageFile(null);
                setTimeNotImageFile(null);
                setSchemaDataText("");
                setSchemaDataError("");
            }
        }
    }, [visible, editingRecord, form, establishmentTypeId, establishmentTypes]);

    const handleSave = async () => {
        try {
            const values = await form.validateFields();

            let parsedSchemaData: Record<string, any> | undefined;
            const cleanedSchema = schemaDataText
                .trim()
                .replace(/^<script\b[^>]*>\s*/i, '')
                .replace(/\s*<\/script>\s*$/i, '')
                .trim();
            if (cleanedSchema) {
                try {
                    parsedSchemaData = JSON.parse(cleanedSchema);
                } catch (e) {
                    const msg = "JSON-LD Schema is not valid JSON. Paste only the JSON object (script tags are stripped automatically).";
                    setSchemaDataError(msg);
                    setActiveTab("seo");
                    message.error(msg);
                    return;
                }
            }
            setSchemaDataError("");

            // Pull pricingPlanIds from form state directly. validateFields can omit it
            // if the Form.Item inside the Pricing Plans tab never mounted (Antd Tabs
            // lazy-renders tab panes), in which case the parent handler's
            // `Array.isArray` check skips appending and the backend never sees the field.
            const pricingPlanIdsRaw = form.getFieldValue('pricingPlanIds');
            const pricingPlanIds = Array.isArray(pricingPlanIdsRaw) ? pricingPlanIdsRaw : [];

            const sanitizedValues = {
                ...values,
                zipCode: values.zipCode ? String(values.zipCode) : values.zipCode,
                phone: values.phone ? String(values.phone) : values.phone,
                code: values.code ? String(values.code) : values.code,
                availableLanguages: Array.from(new Set([...(values.availableLanguages || []), 'en'])),
                pricingPlanIds,
            };

            if (sanitizedValues.heroContent) {
                sanitizedValues.heroContent = {
                    ...sanitizedValues.heroContent,
                    seo: {
                        ...(sanitizedValues.heroContent.seo || {}),
                        schemaData: parsedSchemaData,
                    },
                };
            }

            onSave(sanitizedValues);
        } catch (error) {
            console.error("Validation failed:", error);
        }
    };

    const normFile = (e: any) => {
        if (Array.isArray(e)) return e;
        return e?.fileList;
    };

    // --- Image handlers ---
    const makeImageChangeHandler = (
        setFile: React.Dispatch<any>,
        fieldName: string
    ) => ({ fileList }: any) => {
        const currentFile = fileList.length > 0 ? fileList[0] : null;
        setFile(currentFile);
        if (currentFile && currentFile.originFileObj) {
            const realFile = currentFile.originFileObj as File;
            const blob = new Blob([realFile], { type: realFile.type });
            form.setFieldsValue({
                heroContent: { ...form.getFieldValue('heroContent'), [fieldName]: blob }
            });
        } else {
            const hc = form.getFieldValue('heroContent') || {};
            delete hc[fieldName];
            form.setFieldsValue({ heroContent: hc });
        }
    };

    const makeImageRemoveHandler = (
        setFile: React.Dispatch<any>,
        fieldName: string
    ) => () => {
        setFile(null);
        const hc = form.getFieldValue('heroContent') || {};
        delete hc[fieldName];
        form.setFieldsValue({ heroContent: hc });
        return true;
    };

    const handleHeroImageChange = makeImageChangeHandler(setHeroImageFile, 'heroImageUrl');
    const handleHeroImageRemove = makeImageRemoveHandler(setHeroImageFile, 'heroImageUrl');

    // TimeNot image handlers — separate from heroContent so the blob lands under
    // form.timeNotContent.imageUrl, which the parent serializer (EstablishmentLocationList
    // / ImprovedIndex) picks up as a sibling multipart field.
    const handleTimeNotImageChange = ({ fileList }: any) => {
        const currentFile = fileList.length > 0 ? fileList[0] : null;
        setTimeNotImageFile(currentFile);
        if (currentFile && currentFile.originFileObj) {
            const realFile = currentFile.originFileObj as File;
            const blob = new Blob([realFile], { type: realFile.type });
            form.setFieldsValue({
                timeNotContent: { ...form.getFieldValue('timeNotContent'), imageUrl: blob }
            });
        } else {
            const tn = form.getFieldValue('timeNotContent') || {};
            delete tn.imageUrl;
            form.setFieldsValue({ timeNotContent: tn });
        }
    };
    const handleTimeNotImageRemove = () => {
        setTimeNotImageFile(null);
        const tn = form.getFieldValue('timeNotContent') || {};
        delete tn.imageUrl;
        form.setFieldsValue({ timeNotContent: tn });
        return true;
    };

    // Hoisted from below the tab definitions because the Airport Content tab now also
    // renders a per-locale TimeNot translation table that depends on activeLangs.
    const activeLangs = useMemo(
        () => languageOptions.filter(l => selectedLanguages.includes(l.code)),
        [languageOptions, selectedLanguages]
    );

    // --- Section header with inline toggle + color ---
    const sectionHeader = (key: string, label: string, defaultColor?: string) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '16px 0 12px' }}>
            <Form.Item name={["heroContent", "sectionVisibility", key]} valuePropName="checked" noStyle>
                <Switch size="small" defaultChecked />
            </Form.Item>
            <span style={{ fontSize: 14, fontWeight: 600, color: 'rgba(0, 0, 0, 0.88)' }}>{label}</span>
            <div style={{ flex: 1, height: 1, backgroundColor: '#f0f0f0' }} />
            {defaultColor && (
                <Form.Item name={["heroContent", "sectionColors", key]} noStyle>
                    <Input placeholder={defaultColor} size="small" style={{ width: 90 }} />
                </Form.Item>
            )}
        </div>
    );

    // --- Tab content builders ---

    const basicInfoTab = (
        <>
            <Row gutter={16}>
                <Col span={24}>
                    <Form.Item
                        label="Establishment Types"
                        name="establishmentTypeIds"
                        rules={[{ required: true, message: "Please select at least one type" }]}
                        tooltip="Select all types that apply (e.g., Airport + Airline)"
                    >
                        <Select
                            mode="multiple"
                            placeholder="Select types (Airport, Airline, Hotel, etc.)"
                            options={establishmentTypes.map(type => ({ label: type.name, value: type.id }))}
                        />
                    </Form.Item>
                </Col>
            </Row>
            <Row gutter={16}>
                <Col span={12}>
                    <Form.Item label="Specific Type" name="specificType" tooltip="Options are based on selected establishment types">
                        <Select
                            placeholder="Select specific type"
                            disabled={specificTypeOptions.length === 0}
                            options={specificTypeOptions.map(o => ({ label: o, value: o }))}
                        />
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item label="Establishment Name" name="name" rules={[{ required: true, message: "Please enter establishment name" }]}>
                        <Input placeholder="e.g., LAX Airport, JFK Airport" />
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item label="Establishment Code" name="code" tooltip="Optional code (e.g., LAX, JFK, SFO)">
                        <Input placeholder="e.g., LAX" />
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item label="Icon" name="icon" valuePropName="fileList" getValueFromEvent={normFile}>
                        <Upload beforeUpload={() => false} maxCount={1} accept=".svg,.png,.jpg,.jpeg,.webp" listType="picture">
                            <Button icon={<UploadOutlined />}>Upload Icon</Button>
                        </Upload>
                    </Form.Item>
                </Col>
            </Row>

            <Divider style={{ margin: '12px 0' }}>Address & Contact</Divider>
            <Row gutter={16}>
                <Col span={24}>
                    <Form.Item label="Street Address" name="address">
                        <Input placeholder="e.g., 1 World Way" />
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item label="City" name="city"><Input placeholder="e.g., Los Angeles" /></Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item label="State/Province" name="state"><Input placeholder="e.g., California" /></Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item label="Country" name="country"><Input placeholder="e.g., United States" /></Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item label="ZIP/Postal Code" name="zipCode"><Input placeholder="e.g., 90045" /></Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item label="Phone" name="phone"><Input placeholder="e.g., +1-310-646-5252" /></Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item label="Email" name="email"><Input placeholder="e.g., info@lax.com" /></Form.Item>
                </Col>
            </Row>

            <Row gutter={16} style={{ marginTop: 16 }}>
                <Col span={12}>
                    <Form.Item name="enableLandingPage" valuePropName="checked">
                        <Checkbox
                            disabled={loading}
                            onChange={(e) => {
                                const checked = e.target.checked;
                                form.setFieldsValue({ enableLandingPage: checked });
                                if (!checked) {
                                    setHeroImageFile(null);
                                    form.setFieldsValue({ heroContent: {} });
                                }
                            }}
                        >
                            Enable Landing Page
                        </Checkbox>
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item name="showInFooter" valuePropName="checked">
                        <Checkbox disabled={loading}>
                            Show in Footer
                        </Checkbox>
                    </Form.Item>
                </Col>
            </Row>
        </>
    );

    const landingPageTab = (
        <>
            <Form.Item
                label="Available Languages"
                name="availableLanguages"
                tooltip="Select which languages should be available for this establishment's landing page"
            >
                <Checkbox.Group
                    options={languageOptions.map((lang) => ({
                        label: <span>{lang.label}{lang.code === "en" ? " (Required)" : ""}</span>,
                        value: lang.code,
                        disabled: lang.code === "en",
                    }))}
                    onChange={(values) => {
                        const next = Array.from(new Set([...(values as string[]), "en"]));
                        form.setFieldsValue({ availableLanguages: next });
                    }}
                />
            </Form.Item>
            <Form.Item
                label="URL Slug"
                name="slug"
                tooltip="URL-friendly identifier (e.g., 'lax', 'jfk'). Lowercase only."
                normalize={(value: string) => (value ? value.toLowerCase() : value)}
            >
                <Input placeholder="e.g., lax" />
            </Form.Item>

            <Divider style={{ margin: '12px 0' }}>Hero Section</Divider>
            <Row gutter={16}>
                <Col span={24}>
                    <Form.Item label="Hero Title" name={["heroContent", "title"]}>
                        <Input placeholder="e.g., LAX Lost and Found" />
                    </Form.Item>
                </Col>
                <Col span={24}>
                    <Form.Item label="Hero Subtitle" name={["heroContent", "subtitle"]}>
                        <Input.TextArea rows={2} placeholder="e.g., Los Angeles International Airport - Track your lost items" />
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item label="Background Color" name={["heroContent", "backgroundColor"]}>
                        <Input placeholder="e.g., #003087" />
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item label="Primary CTA Text" name={["heroContent", "primaryCtaText"]}>
                        <Input placeholder="e.g., File a Claim" />
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item label="Primary CTA Link" name={["heroContent", "primaryCtaLink"]}>
                        <Input placeholder="e.g., /file-a-claim" />
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item label="Secondary CTA Text" name={["heroContent", "secondaryCtaText"]}>
                        <Input placeholder="e.g., Contact Us" />
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item label="Secondary CTA Link" name={["heroContent", "secondaryCtaLink"]}>
                        <Input placeholder="e.g., /contact" />
                    </Form.Item>
                </Col>
                <Col span={24}>
                    <Form.Item label="Hero Image" name={["heroContent", "heroImageUrl"]}>
                        <Upload
                            name="heroImage"
                            listType="picture"
                            fileList={heroImageFile ? [heroImageFile] : []}
                            accept=".png,.jpg,.jpeg,.webp"
                            maxCount={1}
                            onChange={handleHeroImageChange}
                            onRemove={handleHeroImageRemove}
                            beforeUpload={imageBeforeUpload as any}
                        >
                            <Button icon={<UploadOutlined />}>Upload Hero Image</Button>
                        </Upload>
                    </Form.Item>
                </Col>
            </Row>
        </>
    );

    const airportContentTab = (
        <>
            <Row gutter={16}>
                <Col span={12}>
                    <Form.Item label="Airport Code" name={["heroContent", "airportCode"]}>
                        <Input placeholder="e.g., SFO" />
                    </Form.Item>
                </Col>
            </Row>

            <Form.Item label="Hero Descriptions (one per paragraph)">
                <Form.List name={["heroContent", "descriptions"]}>
                    {(fields, { add, remove }) => (
                        <>
                            {fields.map(({ key, name, ...restField }) => (
                                <Row key={key} gutter={8} align="middle" style={{ marginBottom: 8 }}>
                                    <Col flex="auto">
                                        <Form.Item {...restField} name={name} noStyle>
                                            <Input.TextArea rows={2} placeholder="Description paragraph" />
                                        </Form.Item>
                                    </Col>
                                    <Col>
                                        <MinusCircleOutlined onClick={() => remove(name)} style={{ color: '#ff4d4f' }} />
                                    </Col>
                                </Row>
                            ))}
                            <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>Add Description</Button>
                        </>
                    )}
                </Form.List>
            </Form.Item>

            {sectionHeader("searchDetails", "Search Details Grid", "#f5f7fa")}
            <Form.Item label="Section Title" name={["heroContent", "searchDetails", "sectionTitle"]}>
                <Input placeholder="e.g., What You Can Search for at SFO Lost and Found" />
            </Form.Item>

            <Form.Item label="Search Columns">
                <Form.List name={["heroContent", "searchDetails", "columns"]}>
                    {(fields, { add, remove }) => (
                        <>
                            {fields.map(({ key, name, ...restField }) => (
                                <div key={key} style={{ border: '1px solid #d9d9d9', padding: 12, borderRadius: 6, marginBottom: 12 }}>
                                    <Row gutter={8} align="middle">
                                        <Col flex="auto">
                                            <Form.Item {...restField} name={[name, "title"]} label="Column Title" style={{ marginBottom: 8 }}>
                                                <Input placeholder="e.g., Electronics" />
                                            </Form.Item>
                                        </Col>
                                        <Col>
                                            <MinusCircleOutlined onClick={() => remove(name)} style={{ color: '#ff4d4f' }} />
                                        </Col>
                                    </Row>
                                    <Row gutter={8} style={{ marginBottom: 8 }}>
                                        <Col span={12}>
                                            <Form.Item {...restField} name={[name, "icon"]} label="Icon (SVG)" style={{ marginBottom: 0 }}
                                                valuePropName="fileList" getValueFromEvent={normFile}
                                            >
                                                <Upload
                                                    accept=".svg,.png"
                                                    maxCount={1}
                                                    listType="picture"
                                                    beforeUpload={() => false}
                                                >
                                                    <Button icon={<UploadOutlined />} size="small">Upload Icon</Button>
                                                </Upload>
                                            </Form.Item>
                                        </Col>
                                        <Col span={12}>
                                            <Form.Item {...restField} name={[name, "color"]} label="Color" style={{ marginBottom: 0 }}
                                                getValueFromEvent={(color: any) => typeof color === 'string' ? color : color?.toHexString?.() || color}
                                            >
                                                <ColorPicker format="hex" showText />
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                    <Form.List name={[name, "items"]}>
                                        {(itemFields, { add: addItem, remove: removeItem }) => (
                                            <>
                                                {itemFields.map(({ key: iKey, name: iName, ...iRest }) => (
                                                    <Row key={iKey} gutter={8} align="middle" style={{ marginBottom: 4 }}>
                                                        <Col flex="auto">
                                                            <Form.Item {...iRest} name={iName} noStyle>
                                                                <Input placeholder="Item name" />
                                                            </Form.Item>
                                                        </Col>
                                                        <Col>
                                                            <MinusCircleOutlined onClick={() => removeItem(iName)} style={{ color: '#ff4d4f', fontSize: 12 }} />
                                                        </Col>
                                                    </Row>
                                                ))}
                                                <Button type="dashed" onClick={() => addItem()} size="small" icon={<PlusOutlined />} style={{ marginTop: 4 }}>
                                                    Add Item
                                                </Button>
                                            </>
                                        )}
                                    </Form.List>
                                </div>
                            ))}
                            <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>Add Column</Button>
                        </>
                    )}
                </Form.List>
            </Form.Item>

            {sectionHeader("timeNot", "Time Is Not On Your Side (CMS Override)", "#fff7e6")}
            <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 12 }}>
                Overrides the canonical "Time is not on your side…" section on this
                location's landing page. Leave blank to fall back to the default i18n copy.
            </div>
            <Row gutter={16}>
                <Col span={24}>
                    <Form.Item label="Title" name={["timeNotContent", "title"]}>
                        <Input placeholder="e.g., Time is not on your side…" />
                    </Form.Item>
                </Col>
                <Col span={24}>
                    <Form.Item label="Description" name={["timeNotContent", "description"]}>
                        <Input.TextArea rows={3} placeholder="Most airports/establishments dispose of items within 30 days..." />
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item label="Button Text" name={["timeNotContent", "buttonText"]}>
                        <Input placeholder="e.g., File a Claim Now" />
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item label="Section Image" name={["timeNotContent", "imageUrl"]}>
                        <Upload
                            accept=".png,.jpg,.jpeg,.webp"
                            maxCount={1}
                            listType="picture"
                            beforeUpload={imageBeforeUpload}
                            fileList={timeNotImageFile ? [timeNotImageFile] : []}
                            onChange={handleTimeNotImageChange}
                            onRemove={handleTimeNotImageRemove}
                        >
                            <Button icon={<UploadOutlined />}>Upload Image</Button>
                        </Upload>
                    </Form.Item>
                </Col>
            </Row>

            <Divider style={{ margin: '12px 0' }}>Localized TimeNot Content</Divider>
            {activeLangs.length <= 1 ? (
                <div style={{ color: '#999', textAlign: 'center', padding: '12px 0' }}>
                    Enable more languages in the Landing Page tab to add TimeNot translations.
                </div>
            ) : (
                <Tabs
                    type="card"
                    size="small"
                    items={activeLangs.map((lang) => ({
                        key: lang.code,
                        label: lang.label,
                        children: (
                            <Row gutter={16}>
                                <Col span={24}>
                                    <Form.Item label="Title" name={["timeNotContentTranslations", lang.code, "title"]}>
                                        <Input placeholder={`TimeNot title in ${lang.label}`} />
                                    </Form.Item>
                                </Col>
                                <Col span={24}>
                                    <Form.Item label="Description" name={["timeNotContentTranslations", lang.code, "description"]}>
                                        <Input.TextArea rows={3} placeholder={`TimeNot description in ${lang.label}`} />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item label="Button Text" name={["timeNotContentTranslations", lang.code, "buttonText"]}>
                                        <Input placeholder={`Button text in ${lang.label}`} />
                                    </Form.Item>
                                </Col>
                            </Row>
                        ),
                    }))}
                />
            )}

            {sectionHeader("aboutSection", "About Section (H3 + paragraph below search table)", "#ffffff")}
            <Form.Item label="Heading (H3)" name={["heroContent", "aboutSection", "heading"]}>
                <Input placeholder="e.g., Navigating LAX's 9 Terminals" />
            </Form.Item>
            <Form.Item label="Body Paragraph" name={["heroContent", "aboutSection", "body"]}>
                <Input.TextArea rows={5} placeholder="Long-form descriptive paragraph about this airport..." />
            </Form.Item>

            {sectionHeader("disclaimer", "Disclaimer", "#f7f9fc")}
            <Form.Item label="Disclaimer Text" name={["heroContent", "disclaimer"]}>
                <Input.TextArea rows={3} placeholder="e.g., Lost & Found Concierge is not affiliated with SFO Airport..." />
            </Form.Item>

            {sectionHeader("recoveryLinks", "Recovery Quick Links", "#f8fafc")}
            <Form.Item label="Links shown as H3 cards (e.g., Lost Phone, Lost Wallet)">
                <Form.List name={["heroContent", "recoveryLinks"]}>
                    {(fields, { add, remove }) => (
                        <>
                            {fields.map(({ key, name, ...restField }) => (
                                <Row key={key} gutter={8} align="middle" style={{ marginBottom: 8 }}>
                                    <Col span={10}>
                                        <Form.Item {...restField} name={[name, "label"]} noStyle>
                                            <Input placeholder="Label (e.g., Lost Phone)" />
                                        </Form.Item>
                                    </Col>
                                    <Col flex="auto">
                                        <Form.Item {...restField} name={[name, "url"]} noStyle>
                                            <Input placeholder="URL (e.g., /file-a-claim)" />
                                        </Form.Item>
                                    </Col>
                                    <Col>
                                        <MinusCircleOutlined onClick={() => remove(name)} style={{ color: '#ff4d4f' }} />
                                    </Col>
                                </Row>
                            ))}
                            <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>Add Recovery Link</Button>
                        </>
                    )}
                </Form.List>
            </Form.Item>

            {sectionHeader("contactInfo", "Contact Info Override", "#ffffff")}
            <Row gutter={16}>
                <Col span={8}>
                    <Form.Item label="Lost & Found Phone" name={["heroContent", "contactInfo", "lostFoundPhone"]}>
                        <Input placeholder="e.g., (305) 876-7000" />
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item label="Lost & Found Hours" name={["heroContent", "contactInfo", "lostFoundHours"]}>
                        <Input placeholder="e.g., Open 24/7" />
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item label="Lost & Found Location" name={["heroContent", "contactInfo", "lostFoundLocation"]}>
                        <Input placeholder="e.g., Terminal 2, Level 1" />
                    </Form.Item>
                </Col>
            </Row>

        </>
    );

    const seoTranslationsTab = (
        <>
            <Divider style={{ margin: '0 0 12px' }}>SEO Settings</Divider>
            <Row gutter={16}>
                <Col span={12}>
                    <Form.Item label="SEO Title" name={["heroContent", "seo", "title"]}>
                        <Input placeholder="e.g., SFO Lost and Found | Track Lost Items" />
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item label="SEO Keywords" name={["heroContent", "seo", "keywords"]}>
                        <Input placeholder="e.g., SFO lost and found, airport lost items" />
                    </Form.Item>
                </Col>
                <Col span={24}>
                    <Form.Item label="SEO Description" name={["heroContent", "seo", "description"]}>
                        <Input.TextArea rows={2} placeholder="e.g., Lost something at SFO Airport? Track and recover..." />
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item label="Canonical URL" name={["heroContent", "seo", "canonicalUrl"]}>
                        <Input placeholder="e.g., https://lostandfoundconcierge.com/airports/sfo" />
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item label="OG Title" name={["heroContent", "seo", "ogTitle"]}>
                        <Input placeholder="Open Graph title" />
                    </Form.Item>
                </Col>
                <Col span={24}>
                    <Form.Item label="OG Description" name={["heroContent", "seo", "ogDescription"]}>
                        <Input.TextArea rows={2} placeholder="Open Graph description" />
                    </Form.Item>
                </Col>
                <Col span={24}>
                    <Form.Item
                        label="JSON-LD Schema (structured data injected into <head>)"
                        tooltip="Paste a complete JSON-LD object (no <script> tags). Validated as JSON on save. Leave blank to auto-generate Service schema."
                        validateStatus={schemaDataError ? "error" : undefined}
                        help={schemaDataError || undefined}
                    >
                        <Input.TextArea
                            rows={12}
                            value={schemaDataText}
                            onChange={(e) => {
                                setSchemaDataText(e.target.value);
                                if (schemaDataError) setSchemaDataError("");
                            }}
                            placeholder={'{\n  "@context": "https://schema.org",\n  "@type": "Service",\n  ...\n}'}
                            style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 12 }}
                        />
                    </Form.Item>
                </Col>
            </Row>

            <Divider style={{ margin: '12px 0' }}>Localized Hero Content</Divider>
            {activeLangs.length <= 1 ? (
                <div style={{ color: '#999', textAlign: 'center', padding: '16px 0' }}>
                    Enable more languages in the Landing Page tab to add translations.
                </div>
            ) : (
                <Tabs
                    type="card"
                    size="small"
                    items={activeLangs.map((lang) => ({
                        key: lang.code,
                        label: lang.label,
                        children: (
                            <Row gutter={16}>
                                <Col span={24}>
                                    <Form.Item label="Title" name={["heroContentTranslations", lang.code, "title"]}>
                                        <Input placeholder={`Hero title in ${lang.label}`} />
                                    </Form.Item>
                                </Col>
                                <Col span={24}>
                                    <Form.Item label="Subtitle" name={["heroContentTranslations", lang.code, "subtitle"]}>
                                        <Input.TextArea rows={2} placeholder={`Hero subtitle in ${lang.label}`} />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item label="Primary CTA Text" name={["heroContentTranslations", lang.code, "primaryCtaText"]}>
                                        <Input placeholder={`CTA button text in ${lang.label}`} />
                                    </Form.Item>
                                </Col>
                            </Row>
                        ),
                    }))}
                />
            )}
        </>
    );

    const howItWorksFaqTab = (
        <>
            {sectionHeader("howItWorks", "How It Works", "#ffffff")}
            <Form.Item label="Section Title Override" name={["heroContent", "howItWorks", "title"]}>
                <Input placeholder="e.g., How Our MIA Lost & Found Assistance Works (leave blank for auto-generated)" />
            </Form.Item>
            <Form.Item label="Steps">
                <Form.List name={["heroContent", "howItWorks", "steps"]}>
                    {(fields, { add, remove }) => (
                        <>
                            {fields.map(({ key, name, ...restField }) => (
                                <div key={key} style={{ border: '1px solid #d9d9d9', padding: 12, borderRadius: 6, marginBottom: 12 }}>
                                    <Row gutter={8} align="middle">
                                        <Col flex="auto">
                                            <Form.Item {...restField} name={[name, "title"]} label="Step Title" style={{ marginBottom: 8 }}>
                                                <Input placeholder="e.g., File Your Claim" />
                                            </Form.Item>
                                        </Col>
                                        <Col>
                                            <MinusCircleOutlined onClick={() => remove(name)} style={{ color: '#ff4d4f' }} />
                                        </Col>
                                    </Row>
                                    <Form.Item {...restField} name={[name, "content"]} label="Step Content" style={{ marginBottom: 0 }}>
                                        <Input.TextArea rows={2} placeholder="Describe this step..." />
                                    </Form.Item>
                                </div>
                            ))}
                            <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>Add Step</Button>
                        </>
                    )}
                </Form.List>
            </Form.Item>

            {sectionHeader("faq", "FAQs", "#000000")}
            <Form.Item label="Airport-specific FAQ entries (leave empty for generic FAQs)">
                <Form.List name={["heroContent", "faqs"]}>
                    {(fields, { add, remove }) => (
                        <>
                            {fields.map(({ key, name, ...restField }) => (
                                <div key={key} style={{ border: '1px solid #d9d9d9', padding: 12, borderRadius: 6, marginBottom: 12 }}>
                                    <Row gutter={8} align="middle">
                                        <Col flex="auto">
                                            <Form.Item {...restField} name={[name, "question"]} label="Question" style={{ marginBottom: 8 }}>
                                                <Input placeholder="e.g., How do I report a lost item at MIA?" />
                                            </Form.Item>
                                        </Col>
                                        <Col>
                                            <MinusCircleOutlined onClick={() => remove(name)} style={{ color: '#ff4d4f' }} />
                                        </Col>
                                    </Row>
                                    <Form.Item {...restField} name={[name, "answer"]} label="Answer" style={{ marginBottom: 0 }}>
                                        <Input.TextArea rows={3} placeholder="Answer to the question..." />
                                    </Form.Item>
                                </div>
                            ))}
                            <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>Add FAQ</Button>
                        </>
                    )}
                </Form.List>
            </Form.Item>

            <Divider style={{ margin: '12px 0' }}>Other Sections</Divider>
            <Row gutter={16}>
                <Col span={8}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Form.Item name={["heroContent", "sectionVisibility", "pricing"]} valuePropName="checked" noStyle>
                            <Switch size="small" defaultChecked />
                        </Form.Item>
                        <span style={{ fontSize: 13, fontWeight: 500 }}>Pricing</span>
                    </div>
                </Col>
                <Col span={8}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Form.Item name={["heroContent", "sectionVisibility", "reviews"]} valuePropName="checked" noStyle>
                            <Switch size="small" defaultChecked />
                        </Form.Item>
                        <span style={{ fontSize: 13, fontWeight: 500 }}>Reviews</span>
                    </div>
                </Col>
            </Row>
        </>
    );

    // Pricing plans tab — always available. Empty selection = show all active plans (default,
    // backwards compatible). Non-empty = restrict landing page + checkout to those plans.
    const pricingPlansTab = (
        <>
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>Pricing Plans for this Establishment</div>
            <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 12 }}>
                Pick which pricing plans appear on this establishment's landing page and during checkout.
                <br />
                <strong>Leave empty to use all active plans</strong> (default behavior — no scoping).
            </div>
            <Divider style={{ margin: '12px 0' }} />
            <Form.Item name="pricingPlanIds" valuePropName="value">
                <Checkbox.Group style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {(allPricingPlans || []).filter(p => p.isActive).map(plan => (
                        <div key={plan.id} style={{ border: '1px solid #f0f0f0', borderRadius: 6, padding: 12, background: '#fafafa' }}>
                            <Checkbox value={plan.id}>
                                <span style={{ fontWeight: 600 }}>{plan.title}</span>
                                {plan.amount != null && (
                                    <span style={{ color: '#52c41a', marginLeft: 8, fontWeight: 500 }}>${plan.amount}</span>
                                )}
                                {plan.days != null && (
                                    <span style={{ color: '#8c8c8c', marginLeft: 8, fontSize: 12 }}>· {plan.days} days</span>
                                )}
                            </Checkbox>
                        </div>
                    ))}
                    {(!allPricingPlans || allPricingPlans.length === 0) && (
                        <div style={{ color: '#9b9b9b', textAlign: 'center', padding: '16px 0' }}>
                            No pricing plans found. Create some in the Pricing Plans page first.
                        </div>
                    )}
                </Checkbox.Group>
            </Form.Item>
        </>
    );

    const tabItems = [
        { key: "basic", label: "Basic Info", children: basicInfoTab },
        ...(enableLandingPage ? [
            { key: "landing", label: "Landing Page", children: landingPageTab },
            { key: "airport", label: "Airport Content", children: airportContentTab },
            { key: "hiwfaq", label: "How It Works & FAQs", children: howItWorksFaqTab },
            { key: "seo", label: "SEO & Translations", children: seoTranslationsTab },
        ] : []),
        { key: "pricing", label: "Pricing Plans", children: pricingPlansTab },
    ];

    return (
        <Modal
            title={editingRecord ? "Edit Establishment" : "Add Establishment"}
            open={visible}
            onCancel={onCancel}
            width={960}
            footer={[
                <Button key="cancel" onClick={onCancel}>Cancel</Button>,
                <Button key="save" type="primary" loading={loading} onClick={handleSave}>
                    {editingRecord ? "Update" : "Create"}
                </Button>,
            ]}
        >
            <Form form={form} layout="vertical" initialValues={{ status: ActivationStatus.Active, enableLandingPage: false, showInFooter: false }}>
                <Form.Item name="id" hidden><Input /></Form.Item>
                <Form.Item name="establishmentTypeId" hidden initialValue={establishmentTypeId}><Input /></Form.Item>
                <Form.Item name="status" hidden initialValue={ActivationStatus.Active}><Input /></Form.Item>

                <Tabs
                    activeKey={activeTab}
                    onChange={(key) => setActiveTab(key)}
                    items={tabItems}
                    style={{ marginTop: -8 }}
                />
            </Form>
        </Modal>
    );
};

export default EstablishmentLocationModal;
